import React, { useState } from "react";
import { Loader, Brain } from "lucide-react";
import { api, friendlyAxiosError } from "../../lib/api";
import ProgressMeter from "../ProgressMeter";

const MINDSET_PRESETS = [
  "About to walk into a 40-person networking mixer where I know no one",
  "Cold DM-ing a VP whose work I admire",
  "Following up with someone who ghosted me 2 weeks ago",
  "Asking for a referral from a former colleague",
  "Pitching myself at a conference coffee chat",
];

export default function MindsetPanel() {
  const [situation, setSituation] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const submit = async (overrideText) => {
    const text = (overrideText ?? situation).trim();
    if (text.length < 4) {
      setError("Describe the moment Resmo should coach you through.");
      return;
    }
    setError("");
    setLoading(true);
    setSituation(text);
    try {
      const { data } = await api.post("/network/mindset", { situation: text });
      setData(data);
    } catch (e) {
      setError(friendlyAxiosError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="mindset-panel">
      <div className="rm-card-static p-5 md:p-6" style={{ background: "#0F1B2D", color: "white" }}>
        <div className="rm-label mb-1" style={{ color: "#FFC145" }}>· Step in grounded</div>
        <h2 className="font-display text-2xl uppercase mb-2">What's the moment?</h2>
        <p className="text-sm mb-4" style={{ color: "#CBD5E1" }}>
          Tell Resmo what you're walking into — they'll hand you a reframe,
          word swaps, and a 2-minute drill before you walk in.
        </p>
        <textarea className="rm-input" style={{ background: "white", color: "#0F1B2D" }} rows={3}
                  placeholder="e.g. About to send a cold email to a head of design I met once 4 years ago"
                  value={situation} onChange={(e) => setSituation(e.target.value)}
                  data-testid="mindset-situation-input" />
        <div className="flex flex-wrap gap-1.5 mt-3">
          {MINDSET_PRESETS.map((p, i) => (
            <button key={i} onClick={() => submit(p)}
                    className="font-mono-rm text-[10px] uppercase tracking-widest px-2 py-1"
                    style={{ background: "#FFC145", color: "#0F1B2D", border: "2px solid #FFC145",
                             fontWeight: 700, borderRadius: 4 }}
                    data-testid={`mindset-preset-${i}`}>
              {p.length > 50 ? `${p.slice(0, 50)}…` : p}
            </button>
          ))}
        </div>
        {error && <div className="mt-3 font-mono-rm text-sm" style={{ color: "#FCA5A5" }} data-testid="mindset-error">{error}</div>}
        <button onClick={() => submit()} disabled={loading} className="rm-btn rm-btn-primary mt-4"
                data-testid="mindset-submit-btn">
          {loading ? <Loader size={16} className="animate-spin" /> : <Brain size={16} />}
          {loading ? "Resmo is coaching…" : "Coach me (5 credits)"}
        </button>
        {loading && (
          <div className="mt-3 max-w-sm">
            <ProgressMeter expectedSeconds={20} label="Coaching" testid="mindset-progress-meter" />
          </div>
        )}
      </div>

      {data && (
        <div className="space-y-4" data-testid="mindset-results">
          {data.reframe && (
            <div className="rm-card-static p-5" data-testid="mindset-reframe">
              <div className="rm-label mb-1" style={{ color: "#FF6A00" }}>· The reframe</div>
              <p className="text-sm md:text-base leading-relaxed">{data.reframe}</p>
            </div>
          )}
          {(data.word_swaps || []).length > 0 && (
            <div className="rm-card-static p-5" data-testid="mindset-swaps">
              <div className="rm-label mb-3" style={{ color: "#FF6A00" }}>· Word swaps</div>
              <div className="space-y-2">
                {data.word_swaps.map((s, i) => (
                  <div key={i} className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-2" style={{ background: "#FEF2F2", border: "2px solid #EF4444" }}>
                      <div className="font-mono-rm text-[9px] uppercase tracking-widest" style={{ color: "#991B1B", fontWeight: 700 }}>Avoid</div>
                      <div>{s.avoid}</div>
                    </div>
                    <div className="p-2" style={{ background: "#ECFDF5", border: "2px solid #10B981" }}>
                      <div className="font-mono-rm text-[9px] uppercase tracking-widest" style={{ color: "#065F46", fontWeight: 700 }}>Use</div>
                      <div>{s.use}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(data.two_minute_drill || []).length > 0 && (
            <div className="rm-card-static p-5" data-testid="mindset-drill">
              <div className="rm-label mb-2" style={{ color: "#FF6A00" }}>· 2-minute drill</div>
              <ol className="space-y-1.5 text-sm">
                {data.two_minute_drill.map((d, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="font-display" style={{ color: "#FF6A00" }}>{String(i + 1).padStart(2, "0")}</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {data.courage_line && (
            <div className="rm-card-static p-5" style={{ background: "linear-gradient(135deg,#FF6A00,#B45309)", color: "white" }}
                 data-testid="mindset-courage-line">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1" style={{ color: "#FFC145", fontWeight: 700 }}>· Repeat before you walk in</div>
              <p className="text-base md:text-lg leading-snug italic font-display" style={{ fontFamily: "Georgia, serif" }}>
                "{data.courage_line}"
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
